import { useEffect, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";

import {
  type CarrierTrip,
  useAssignVehicleToTrip,
  useCarrierVehicles,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";

interface AssignVehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trip: Pick<CarrierTrip, "id" | "vehicleId" | "dispatchCenterId">;
  dialogPresentation?: DialogProps["presentation"];
}

const NONE = "__none__";

export function AssignVehicleDialog({
  open,
  onOpenChange,
  trip,
  dialogPresentation,
}: AssignVehicleDialogProps) {
  const vehicles = useCarrierVehicles({ limit: 100 });
  const assign = useAssignVehicleToTrip();
  const scopedVehicles = (vehicles.data?.data ?? []).filter(
    (vehicle) => vehicle.dispatchCenter?.id === trip.dispatchCenterId
  );
  const [vehicleId, setVehicleId] = useState(trip.vehicleId ?? NONE);
  const submitLock = useRef(false);
  const responsive = dialogPresentation === "responsive-sheet";

  useEffect(() => {
    setVehicleId(trip.vehicleId ?? NONE);
  }, [trip.vehicleId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;
    try {
      await assign.mutateAsync({
        tripId: trip.id,
        vehicleId: vehicleId === NONE ? null : vehicleId,
      });
      toast.success(
        vehicleId === NONE ? "Vehicle unassigned." : "Vehicle assigned."
      );
      onOpenChange(false);
    } catch {
      toast.error("Failed to update vehicle.");
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!assign.isPending) onOpenChange(nextOpen);
      }}
      title="Assign vehicle"
      presentation={dialogPresentation}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label
            htmlFor="trip-vehicle-picker"
            className="mb-1 block text-sm font-medium"
          >
            Vehicle
          </label>
          <NativeSelect
            id="trip-vehicle-picker"
            value={vehicleId}
            onChange={(event) => setVehicleId(event.target.value)}
            disabled={vehicles.isLoading || assign.isPending}
          >
            <VehicleOptions vehicles={scopedVehicles} />
          </NativeSelect>
        </div>

        {vehicles.error && (
          <p role="alert" className="text-sm text-destructive">
            {hasHttpStatus(vehicles.error, 403)
              ? "You do not have permission to load vehicles."
              : "Failed to load vehicles. Try again."}
          </p>
        )}

        {assign.isError && (
          <p role="alert" className="text-sm text-destructive">
            Failed to update the Trip vehicle. Try again.
          </p>
        )}

        <div
          className={
            responsive
              ? "sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4"
              : "flex justify-end gap-2"
          }
        >
          <Button
            type="button"
            variant="outline"
            size={responsive ? "touch" : "default"}
            onClick={() => onOpenChange(false)}
            disabled={assign.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={responsive ? "touch" : "default"}
            disabled={
              assign.isPending || vehicles.isLoading || !!vehicles.error
            }
          >
            {assign.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function VehicleOptions({
  vehicles,
}: {
  vehicles: NonNullable<ReturnType<typeof useCarrierVehicles>["data"]>["data"];
}) {
  return (
    <>
      <option value={NONE}>No vehicle</option>
      {vehicles.map((vehicle) => (
        <option key={vehicle.id} value={vehicle.id}>
          {vehicle.code} · {vehicle.make} {vehicle.model}
        </option>
      ))}
    </>
  );
}
